import React, { useState, useEffect, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import type { CreateTarget } from 'actions';
import { Modal, Form, Button, Alert, Badge, Card, ListGroup } from 'react-bootstrap';
import { Event, EventAction } from '../../../../redux/slices/eventsSlice'; // Adjust the path accordingly
import api from '../../../../services/api';
import Select from 'react-select';
import { EditNodeDefinitionReference, type NodeDefinitionReference } from 'edit/reference/EditNodeDefinitionReference';
import { EditConnectionReference, type ConnectionReference } from 'edit/reference/EditConnectionReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { LoadingInput, LoadingSelect } from 'components/forms/LoadingFields';
import { FolderSelect } from 'components/forms/FolderSelect';
import { getQueuesPaginated } from '../../../../redux/slices/lists/queuesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import FormatDateTime from 'components/common/FormatDateTime';
import EventRelatedObjects from './EventRelatedObjects';

interface Worker {
  worker_id: number;
  worker_name: string;
}

interface EventFileEventProps {
  show: boolean;
  onClose: () => void;
  event?: Event | null; // `event` is optional to support creating a new event
  onSave: (updatedEvent: Event) => Promise<any>; // Expect `onSave` to return a promise with the response or error
  // Workspace create pre-target: seeds the zone + folder on create.
  createIn?: CreateTarget;
}

/** Must match FILE_EVENT_SSH_STATUS_MAX_ENTRIES in nap-backend/websockets/websockets_server.js */
const FILE_EVENT_SSH_STATUS_MAX_STORED = 100;

/** Shared default action shape, used both for a brand-new event and to normalize a stored one. */
const DEFAULT_ACTION: EventAction = {
  type: 'submit_node_definition',
  node_definition_id: null,
  queue_id: null,
  parameters: [],
};

function sshStatusBadgeVariant(status: string | undefined): string {
  const s = (status || '').toLowerCase();
  if (s.includes('fail') || s.includes('error') || s.includes('_failed') || s.includes('probe_failed')) {
    return 'danger';
  }
  if (
    s.includes('connected') ||
    s.includes('scan_result') ||
    s.includes('move_success') ||
    s.includes('session_established') ||
    s.includes('connection_recovered')
  ) {
    return 'success';
  }
  if (s.includes('connection_lost')) {
    return 'danger';
  }
  if (s.includes('started') || s.includes('monitor_started')) {
    return 'info';
  }
  if (s.includes('stopped')) {
    return 'warning';
  }
  if (s.includes('empty') || s.includes('scan_empty')) {
    return 'warning';
  }
  return 'secondary';
}

const EventFileEvent: React.FC<EventFileEventProps> = ({ show, onClose, event, onSave, createIn }) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<Event | null>(null);
  const [apiResponse, setApiResponse] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [pollingEnabled, setPollingEnabled] = useState<boolean>(true);
  const reduxError = useAppSelector((state) => state.events.error);

  const { zones, loading: zonesLoadingState } = useAppSelector((state) => state.listOfZones);
  const zonesLoading = !!zonesLoadingState?.getAuthorizedZones;

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const isDirtyRaw = useUnsavedChanges(show && !!formData, formData);
  const isDirty = !!event && isDirtyRaw;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const { queues, loading: queuesLoadingState } = useAppSelector((state) => state.queues);
  const queuesLoading = !!queuesLoadingState?.getQueuesPaginated;

  const normalizeEventType = (rawType: string | undefined, cfg: any) => {
    if (rawType === 'fileEventSSH') {
      return rawType;
    }
    if (rawType === 'fileEvent') {
      return cfg?.connection_id || cfg?.ssh_connection_string ? 'fileEventSSH' : 'fileEvent';
    }
    return rawType || 'fileEvent';
  };

  // Reset formData to null on close so the next open never sees stale baseline
  useEffect(() => {
    if (!show) {
      setFormData(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    if (event) {
      setFormData({
        ...event,
        type: normalizeEventType(event.type, event.config),
        action: {
          ...event.action,
          type: event.action?.type ?? DEFAULT_ACTION.type,
          node_definition_id: event.action?.node_definition_id ?? DEFAULT_ACTION.node_definition_id,
          queue_id: event.action?.queue_id ?? DEFAULT_ACTION.queue_id,
          parameters: Array.isArray(event.action?.parameters) ? event.action.parameters : DEFAULT_ACTION.parameters,
        },
      });
      setPollingEnabled(!!event.config?.poll_interval);
    } else {
      setFormData({
        event_id: 0,
        zone_name: createIn?.zoneName ?? '',
        folder_id: createIn?.folderId ?? null,
        name: '',
        description: '',
        type: 'processEvent',
        action: { ...DEFAULT_ACTION },
        // Seeded file-event shaped whatever the default type is: the file-event
        // fields (and handleSave's poll_interval read) require a config object,
        // so switching Type must find one already there.
        config: {
          worker: '',
          directory: '',
          namingpattern: '',
          move: false,
          movedirectory: '',
          poll_interval: 60,
          min_file_age: 30,
          enableFileEvent: true,
          connection_id: null,
          ssh_connection_status_updates: [],
        },
      });
    }
    setApiResponse(null);
  }, [event, show, createIn]);

  // Fetch worker data from the API
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await api.get('/workers');
        const workerOptions = response.data.workers.map((worker: any) => ({
          worker_id: worker.worker_id,
          worker_name: worker.worker_name,
        }));
        setWorkers(workerOptions);
      } catch (error) {
        console.error('Failed to fetch workers:', error);
      }
    };
    fetchWorkers();
  }, []);

  useEffect(() => {
    if (show) {
      dispatch(getQueuesPaginated({}) as any);
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (show) {
      const action = event ? 'update' : 'create';
      dispatch(getAuthorizedZones({ resourceType: 'events', action }) as any);
    }
  }, [show, dispatch]);

  const handleChange = (field: string, value: any, parentField?: string) => {
    console.log('handleChange - ' + field + ' value: ' + value + ' typeof: ' + typeof value);
    setFormData((prev) => {
      if (!prev) return null;
      if (parentField) {
        return {
          ...prev,
          [parentField]: {
            ...(prev as any)[parentField],
            [field]: value,
          },
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
    console.log('formData: ' + JSON.stringify(formData));
  };

  const handleSave = async () => {
    if (formData) {
      console.log('Saving formData:', formData); // Debug data
      setIsSubmitting(true);
      try {
        const pollInterval = formData.config?.poll_interval;
        const baseConfig: any = {
          ...formData.config,
          poll_interval: pollingEnabled ? (pollInterval && pollInterval > 0 ? pollInterval : 60) : 0,
        };
        // SSH runtime details are always resolved from connection_id server-side.
        delete baseConfig.ssh_connection_string;
        delete baseConfig.ssh_shell;

        const payload: any = {
          ...formData,
          config: baseConfig,
        };

        const result = await onSave(payload);
        if (result && result.meta && result.meta.requestStatus === 'rejected') {
          const data = result.payload || result.error;

          const errorMessage = data?.error || data?.message || 'Failed to save event.';

          setApiResponse({
            type: 'error',
            message: errorMessage,
          });

          return;
        }

        setApiResponse({
          type: 'success',
          message: 'Event saved successfully!',
        });
      } catch (error: any) {
        console.error('Save failed:', error);

        const data = error?.response?.data || error;

        const errorMessage = (typeof data === 'string' && data) || data?.error || data?.message || error?.message || 'Failed to save event.';

        setApiResponse({
          type: 'error',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getQueueValue = () => {
    const queueId = formData?.action?.queue_id;
    if (!queueId) return null;

    const queue = (queues || []).find((q: any) => q.queue_id == queueId);

    if (queue) {
      return {
        value: queue.queue_id,
        label: `${queue.queue_name} (${queue.zone_name})`,
      };
    }
    return {
      value: queueId,
      label: queuesLoading ? 'Loading...' : `Unknown queue (ID: ${queueId})`,
    };
  };

  // The picker deals in whole references; only the id is stored, so inflate it into a key-only
  // sentinel — the empty name is what tells the control to resolve the label itself.
  const actionNodeDefinitionId = formData?.action?.node_definition_id ?? null;
  const actionNodeDefinition = useMemo<NodeDefinitionReference | null>(
    () => (actionNodeDefinitionId !== null ? { node_definition_id: actionNodeDefinitionId, name: '', zone_name: '' } : null),
    [actionNodeDefinitionId]
  );

  // Likewise for the SSH connection, stored as a bare `config.connection_id`.
  const sshConnectionId: number | null = formData?.config?.connection_id || null;
  const sshConnection = useMemo<ConnectionReference | null>(
    () => (sshConnectionId !== null ? { connection_id: sshConnectionId, connection_name: '', connection_type: '' } : null),
    [sshConnectionId]
  );

  // Ingestion normalizes this to an array, but a non-array value could still reach
  // here via a stale formData shape; guard once here rather than at every read site.
  const actionParameters = Array.isArray(formData?.action?.parameters) ? formData.action.parameters : [];

  return (
    <Modal show={show} onHide={handleRequestClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{event ? 'Edit Event' : 'New Event'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Display API response */}
        {apiResponse && (
          <Alert variant={apiResponse.type === 'success' ? 'success' : 'danger'} className="mb-3">
            {apiResponse.message}
          </Alert>
        )}
        <Form>
          {/* Name Field */}
          <LoadingInput
            controlId="name"
            label="Name"
            name="name"
            type="text"
            placeholder=""
            value={formData?.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={isSubmitting}
          />

          {/* Zone Field */}
          <LoadingSelect
            controlId="zone_name"
            label="Zone"
            name="zone_name"
            value={formData?.zone_name || ''}
            onChange={(e) =>
              // Reset the (zone-scoped) folder when the zone changes.
              setFormData((prev) => (prev ? { ...prev, zone_name: e.target.value, folder_id: null } : prev))
            }
            options={(zones || []).map((zone: { name: string }) => ({
              value: zone.name,
              label: zone.name,
            }))}
            isLoading={zonesLoading}
            disabled={isSubmitting}
            placeholder="Select a Zone"
          />

          {/* Folder Field */}
          <Form.Group controlId="event_folder" className="mb-3">
            <Form.Label>Folder</Form.Label>
            <FolderSelect
              value={formData?.folder_id ?? null}
              onChange={(folder_id) => setFormData((prev) => (prev ? { ...prev, folder_id } : prev))}
              zoneName={formData?.zone_name ?? ''}
            />
          </Form.Group>

          {/* Description Field */}
          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              value={formData?.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>

          {/* Type Field */}
          <Form.Group controlId="type">
            <Form.Label>Type</Form.Label>
            <Form.Control
              as="select"
              value={formData?.type || ''}
              onChange={(e) => {
                const nextType = e.target.value;
                setFormData((prev) => {
                  if (!prev) return prev;
                  // Required config fields that render as checkboxes/numbers look already
                  // "set" whether the state is a real value or undefined (e.g. the Move
                  // checkbox reads `formData.config.move || false`), so the user has no
                  // way to notice or fix a missing value through normal interaction.
                  // Seed real defaults here, same as the new-event branch, before
                  // layering the previous config back over them.
                  const nextConfig: any = {
                    worker: '',
                    directory: '',
                    namingpattern: '',
                    move: false,
                    movedirectory: '',
                    poll_interval: 60,
                    min_file_age: 30,
                    enableFileEvent: true,
                    connection_id: null,
                    ssh_connection_status_updates: [],
                    ...(prev.config || {}),
                  };
                  if (nextType === 'fileEvent') {
                    delete nextConfig.connection_id;
                  } else if (nextType === 'fileEventSSH') {
                    delete nextConfig.worker;
                  }
                  return { ...prev, type: nextType, config: nextConfig };
                });
              }}
              disabled={isSubmitting}
            >
              <option value="fileEvent">File Event via Worker</option>
              <option value="fileEventSSH">File Event via SSH</option>
              <option value="processEvent">Process Event</option>
            </Form.Control>
          </Form.Group>

          {event?.event_id && <EventRelatedObjects eventId={event.event_id} />}

          {(formData?.type === 'fileEvent' || formData?.type === 'fileEventSSH') && (
            <>
              <Form.Group controlId="enableFileEvent">
                <Form.Check
                  type="checkbox"
                  label="Enable File Event"
                  checked={formData?.config?.enableFileEvent !== false}
                  onChange={(e) => handleChange('enableFileEvent', e.target.checked, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Polling Checkbox and Interval Input */}
              <Form.Group controlId="polling">
                <Form.Check
                  type="checkbox"
                  label="Enable Polling"
                  checked={pollingEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    // Only rendered for file-event types, which always carry a config. Checked before
                    // the flag flips, so polling can never be on without one.
                    if (!formData?.config) return;
                    setPollingEnabled(enabled);
                    setFormData((prev) => {
                      if (!prev?.config) return prev;
                      if (!enabled) {
                        return {
                          ...prev,
                          config: {
                            ...prev.config,
                            poll_interval: 0,
                          },
                        };
                      }
                      return {
                        ...prev,
                        config: {
                          ...prev.config,
                          poll_interval: prev.config?.poll_interval && prev.config.poll_interval > 0 ? prev.config.poll_interval : 60,
                        },
                      };
                    });
                  }}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {pollingEnabled && (
                <Form.Group controlId="pollInterval" className="mt-2">
                  <Form.Label>Polling Interval (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData?.config?.poll_interval || 60}
                    onChange={(e) => handleChange('poll_interval', e.target.value ? parseInt(e.target.value, 10) : null, 'config')}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              )}
              {formData?.type === 'fileEvent' && (
                <Form.Group controlId="worker">
                  <Form.Label>Worker</Form.Label>
                  <Form.Control
                    as="select"
                    value={formData?.config?.worker || ''}
                    onChange={(e) => handleChange('worker', e.target.value, 'config')}
                    disabled={isSubmitting}
                  >
                    <option value="">Select a worker</option>
                    {workers.map((worker) => (
                      <option key={worker.worker_id} value={worker.worker_id}>
                        {worker.worker_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              )}

              {formData?.type === 'fileEventSSH' && (
                <Form.Group controlId="connection_id">
                  <Form.Label>Connection</Form.Label>
                  <EditConnectionReference
                    type="ssh"
                    placeholder="Search and select an SSH connection..."
                    value={sshConnection}
                    onChange={(c) => handleChange('connection_id', c?.connection_id ?? null, 'config')}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              )}

              {/* Directory Field */}
              <Form.Group controlId="directory">
                <Form.Label>Directory</Form.Label>
                <Form.Control
                  type="text"
                  value={formData?.config?.directory || ''}
                  onChange={(e) => handleChange('directory', e.target.value, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Naming Pattern Field */}
              <Form.Group controlId="namingpattern">
                <Form.Label>Naming Pattern</Form.Label>
                <Form.Control
                  type="text"
                  value={formData?.config?.namingpattern || ''}
                  onChange={(e) => handleChange('namingpattern', e.target.value, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {formData?.type === 'fileEventSSH' && (
                <Form.Group controlId="ssh_connection_status_updates" className="mt-4">
                  <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
                    <Form.Label className="mb-0">SSH connection status</Form.Label>
                    {(formData?.config?.ssh_connection_status_updates || []).length > 0 && (
                      <small className="text-muted">
                        {(formData?.config?.ssh_connection_status_updates || []).length} stored
                        {(formData?.config?.ssh_connection_status_updates || []).length >= FILE_EVENT_SSH_STATUS_MAX_STORED ? ' (at retention limit)' : ''}
                      </small>
                    )}
                  </div>
                  <Form.Text className="text-muted d-block mb-2">
                    Live tail from agents over SSH. Only the most recent {FILE_EVENT_SSH_STATUS_MAX_STORED.toLocaleString()} entries are kept server-side; older
                    rows are dropped automatically.
                  </Form.Text>
                  {(formData?.config?.ssh_connection_status_updates || []).length === 0 ? (
                    <Card className="border-0 bg-light">
                      <Card.Body className="py-3 text-muted small">
                        No status updates yet. They appear here after a worker handling this SSH connection starts polling.
                      </Card.Body>
                    </Card>
                  ) : (
                    <Card className="shadow-sm border">
                      <ListGroup variant="flush" className="small" style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {(formData?.config?.ssh_connection_status_updates || [])
                          .slice()
                          .reverse()
                          .map((entry: any, idx: number) => (
                            <ListGroup.Item key={`${entry?.timestamp || ''}-${idx}`} className="py-2 px-3">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <Badge
                                  bg={sshStatusBadgeVariant(entry?.status)}
                                  className="text-uppercase fw-normal"
                                  style={{
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  {entry?.status || 'unknown'}
                                </Badge>
                                <span className="text-muted ms-auto text-nowrap">{entry?.timestamp ? <FormatDateTime value={entry.timestamp} /> : '-'}</span>
                              </div>
                              {entry?.worker_name ? (
                                <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
                                  Worker: <span className="text-body">{entry.worker_name}</span>
                                </div>
                              ) : null}
                              {entry?.detail ? (
                                <pre
                                  className="mb-0 mt-1 text-body"
                                  style={{
                                    fontSize: '0.78rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    background: 'var(--bs-tertiary-bg)',
                                    borderRadius: 4,
                                    padding: '0.35rem 0.5rem',
                                  }}
                                >
                                  {entry.detail}
                                </pre>
                              ) : null}
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    </Card>
                  )}
                </Form.Group>
              )}

              <Form.Group>
                <Form.Label>Min File Age (seconds)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData?.config?.min_file_age || 30}
                  onChange={(e) => handleChange('min_file_age', parseInt(e.target.value, 10), 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Move Checkbox */}
              <Form.Group controlId="move">
                <Form.Check
                  type="checkbox"
                  label="Move"
                  checked={formData?.config?.move || false}
                  onChange={(e) => handleChange('move', e.target.checked, 'config')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Move Directory Field */}
              {formData?.config?.move && (
                <Form.Group controlId="movedirectory">
                  <Form.Label>Move Directory</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData?.config?.movedirectory || ''}
                    onChange={(e) => handleChange('movedirectory', e.target.value, 'config')}
                    disabled={isSubmitting}
                  />
                </Form.Group>
              )}

              {/* Action Type Field */}
              <Form.Group controlId="actionType">
                <Form.Label>Action Type</Form.Label>
                <Form.Control
                  as="select"
                  value={formData?.action?.type || ''}
                  onChange={(e) => handleChange('type', e.target.value, 'action')}
                  disabled={isSubmitting}
                >
                  <option value="submit_node_definition">Submit Node Definition</option>
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="nodeDefinition">
                <Form.Label>Node Definition</Form.Label>
                <EditNodeDefinitionReference
                  placeholder="Search and select a Node Definition..."
                  value={actionNodeDefinition}
                  onChange={(nd) => handleChange('node_definition_id', nd?.node_definition_id ?? null, 'action')}
                  disabled={isSubmitting}
                />
              </Form.Group>

              <Form.Group controlId="queue">
                <Form.Label>Queue</Form.Label>
                <Select
                  placeholder="Select a queue..."
                  isClearable
                  isLoading={queuesLoading}
                  isDisabled={queuesLoading || isSubmitting}
                  options={(queues || []).map((queue: any) => ({
                    value: queue.queue_id,
                    label: `${queue.queue_name} (${queue.zone_name})`,
                  }))}
                  value={getQueueValue()}
                  onChange={(selectedOption) => {
                    handleChange('queue_id', selectedOption ? selectedOption.value : null, 'action');
                  }}
                />
              </Form.Group>

              <Form.Group controlId="submitTimezone">
                <Form.Label>Submit Timezone</Form.Label>
                <EditTimeZoneReference
                  value={formData?.action?.timezone ?? null}
                  onChange={(v) => handleChange('timezone', v, 'action')}
                  placeholder="use the configured default timezone"
                  disabled={isSubmitting}
                />
              </Form.Group>

              {/* Parameters Section */}
              <Form.Group controlId="parameters">
                <Form.Label>Parameters</Form.Label>
                <div>
                  {actionParameters.map((param, index) => (
                    <div key={index} className="d-flex mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Name"
                        value={param.name || ''}
                        onChange={(e) =>
                          handleChange(
                            'parameters',
                            [...actionParameters.slice(0, index), { ...param, name: e.target.value }, ...actionParameters.slice(index + 1)],
                            'action'
                          )
                        }
                        className="me-2"
                        disabled={isSubmitting}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Value"
                        value={param.value || ''}
                        onChange={(e) =>
                          handleChange(
                            'parameters',
                            [...actionParameters.slice(0, index), { ...param, value: e.target.value }, ...actionParameters.slice(index + 1)],
                            'action'
                          )
                        }
                        disabled={isSubmitting}
                      />
                      <Button
                        variant="danger"
                        onClick={() =>
                          handleChange(
                            'parameters',
                            actionParameters.filter((_, i) => i !== index),
                            'action'
                          )
                        }
                        className="ms-2"
                        disabled={isSubmitting}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="secondary"
                  className="mt-2"
                  onClick={() => handleChange('parameters', [...actionParameters, { name: '', value: '' }], 'action')}
                  disabled={isSubmitting}
                >
                  Add Parameter
                </Button>
              </Form.Group>
            </>
          )}

          {formData?.type === 'processEvent' && <>{/* No other fields required for process events */}</>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
          {event ? 'Save and Close' : 'Create Event'}
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EventFileEvent;
